import React, { useEffect, useState } from "react";
import { Typography, Button, CircularProgress } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { makeStyles } from "tss-react/mui";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import CommonBody from "components/Modules/CommonBody";
import TabTitle from "components/Elements/TabTitle";
import {
	getSiteApplicationDetail,
	patchApplicationDetail,
} from "services/clients/sites/siteApplications/siteApplicationDetails";
import { showError } from "../../../../../redux/common/actions";
import {
	DEFAULT_WORKBOOK_FIELD_ORDER,
	parseWorkbookFieldOrder,
	applyReorder,
} from "helpers/workbookFieldOrder";

const fieldCaptions = (data, defaultCaptions) => ({
	Purpose: data?.purposeCC || defaultCaptions?.purpose,
	Procedure: data?.procedureCC || defaultCaptions?.procedure,
	PossibleHazards:
		data?.possibleHazardsPluralCC || defaultCaptions?.possibleHazardsPlural,
	AdditionalPPE: data?.additionalPPECC || defaultCaptions?.additionalPPE,
	Specification: data?.specificationCC || defaultCaptions?.specification,
	ContaminationControl:
		data?.contaminationControlsCC || defaultCaptions?.contaminationControls,
	OtherInformation:
		data?.otherInformationCC || defaultCaptions?.otherInformation,
	Isolations: data?.isolationsPluralCC || defaultCaptions?.isolationsPlural,
	CorrectiveActions:
		data?.correctiveActionsPluralCC ||
		defaultCaptions?.correctiveActionsPlural,
	Controls: data?.controlsPluralCC || defaultCaptions?.controlsPlural,
	CustomField1: data?.customField1CC || defaultCaptions?.customField1,
	CustomField2: data?.customField2CC || defaultCaptions?.customField2,
	CustomField3: data?.customField3CC || defaultCaptions?.customField3,
});

const useStyles = makeStyles()(() => ({
	formContainer: {
		padding: "20px",
		maxWidth: "900px",
	},
	labelText: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: "18px",
		marginBottom: "8px",
	},
	helperText: {
		fontFamily: "Roboto Condensed",
		fontSize: "14px",
		color: "#666",
		marginBottom: "12px",
	},
	list: {
		display: "flex",
		flexDirection: "column",
		gap: 8,
		maxWidth: 480,
	},
	row: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		border: "1px solid #8F8F8F",
		background: "#FFFFFF",
		borderRadius: 4,
		padding: "8px 12px",
	},
	rowDragging: {
		background: "#D2D2D9",
	},
	position: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: 14,
		width: 24,
	},
	label: {
		fontSize: 14,
	},
	dragIcon: {
		color: "#8F8F8F",
	},
	resetContainer: {
		marginTop: 12,
	},
}));

const WorkbookFieldOrderContent = ({ id, state, setIs404, getError }) => {
	const { t } = useTranslation();
	const { classes, cx } = useStyles();
	const errorDispatch = useDispatch();
	const [order, setOrder] = useState(DEFAULT_WORKBOOK_FIELD_ORDER);
	const [saving, setSaving] = useState(false);
	const [loading, setLoading] = useState(true);
	const [haveData, setHaveData] = useState(false);

	const {
		details: { data },
		defaultCustomCaptionsData,
		isReadOnly,
	} = state;

	const fetchSiteApplicationDetails = async () => {
		try {
			const result = await getSiteApplicationDetail(id);

			if (result.status) {
				setOrder(parseWorkbookFieldOrder(result.data.workbookFieldOrder));
				setLoading(false);
				setHaveData(true);
			} else if (result.status === 404) {
				setIs404(true);
			} else {
				throw new Error(result);
			}
		} catch (err) {
			errorDispatch(showError(t("message.failedToLoadWorkbookFieldOrder")));
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchSiteApplicationDetails();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const captions = fieldCaptions(data, defaultCustomCaptionsData);
	const workbookCaption =
		data?.workbookCC || defaultCustomCaptionsData?.workbook || "Workbook";
	const isDefaultOrder =
		JSON.stringify(order) === JSON.stringify(DEFAULT_WORKBOOK_FIELD_ORDER);

	const saveOrder = async (newOrder, patchValue) => {
		const previousOrder = order;
		setOrder(newOrder);
		setSaving(true);

		const result = await patchApplicationDetail(id, [
			{ op: "replace", path: "workbookFieldOrder", value: patchValue },
		]);

		if (!result.status) {
			setOrder(previousOrder);
			getError(
				result?.data?.detail ||
					t("message.failedToUpdateWorkbookFieldOrder")
			);
		}

		setSaving(false);
	};

	const onDragEnd = ({ source, destination }) => {
		if (!destination || source.index === destination.index) return;

		const newOrder = applyReorder(order, source.index, destination.index);
		saveOrder(newOrder, JSON.stringify(newOrder));
	};

	const onReset = () => saveOrder(DEFAULT_WORKBOOK_FIELD_ORDER, null);

	return (
		<div className="container">
			<TabTitle
				title={`${data?.application?.name ?? ""} ${t(
					"label.workbookOrder",
					{ workbook: workbookCaption }
				)}`}
			/>
			<CommonBody haveData={haveData}>
				<>
					{loading ? (
						<div style={{ textAlign: "center", padding: "40px" }}>
							<CircularProgress />
						</div>
					) : (
						<div className={classes.formContainer}>
							<Typography className={classes.labelText}>
								{t("label.workbookOrder", { workbook: workbookCaption })}
							</Typography>
							<Typography className={classes.helperText}>
								{t("label.workbookFieldOrderHelp", {
									workbook: workbookCaption,
								})}
							</Typography>

							<DragDropContext onDragEnd={onDragEnd}>
								<Droppable droppableId="workbookFieldOrder">
									{(provided) => (
										<div
											ref={provided.innerRef}
											{...provided.droppableProps}
											className={classes.list}
										>
											{order.map((fieldKey, index) => (
												<Draggable
													key={fieldKey}
													draggableId={fieldKey}
													index={index}
													isDragDisabled={isReadOnly || saving}
												>
													{(dragProvided, snapshot) => (
														<div
															ref={dragProvided.innerRef}
															{...dragProvided.draggableProps}
															{...dragProvided.dragHandleProps}
															className={cx(classes.row, {
																[classes.rowDragging]: snapshot.isDragging,
															})}
															data-testid="workbook-field-row"
														>
															<DragIndicatorIcon
																fontSize="small"
																className={classes.dragIcon}
															/>
															<Typography className={classes.position}>
																{index + 1}.
															</Typography>
															<Typography className={classes.label}>
																{captions[fieldKey] ?? fieldKey}
															</Typography>
														</div>
													)}
												</Draggable>
											))}
											{provided.placeholder}
										</div>
									)}
								</Droppable>
							</DragDropContext>

							{!isDefaultOrder && !isReadOnly && (
								<div className={classes.resetContainer}>
									<Button
										variant="outlined"
										onClick={onReset}
										disabled={saving}
										data-testid="workbook-field-order-reset"
									>
										{t("label.workbookFieldOrderReset")}
									</Button>
								</div>
							)}
						</div>
					)}
				</>
			</CommonBody>
		</div>
	);
};

export default WorkbookFieldOrderContent;
